// SEO Title/Meta-Description A/B-Test
// ----------------------------------------------------------------------------
// Hintergrund: Google rendert pro Crawl nur eine Variante. "Echtes" A/B-Testing
// gegen GSC-CTR funktioniert daher nur als sequenzielles Split-Time-Testing:
// Variante A für 14 Tage live -> dann Variante B für 14 Tage -> CTR vergleichen.
//
// Auswertung: Im Longevity-Hackers-Dashboard auf page_views filtern nach
//   page_path LIKE 'variant:title_%'
// Damit weisst du in welchem Zeitraum welche Variante live war. Die CTR holst
// du dir aus der Google Search Console (Filter: Query enthält "rotlicht" oder
// "rabatt", Datum = Variant-Zeitraum).
//
// WICHTIG: Sobald ein Sieger feststeht -> diese Datei aufräumen und die
// Sieger-Variante als statischen Title/Description in Index.tsx setzen.

export type SeoVariantId = "a" | "b";

export interface SeoVariant {
  id: SeoVariantId;
  title: string;
  description: string;
  /** Kurze Beschreibung der Hypothese – nur für Dokumentation. */
  hypothesis: string;
}

// Variante A: aktueller Stand (Mai 2026) – Rabattcode-First
const VARIANT_A: SeoVariant = {
  id: "a",
  title:
    "heilys Rabattcode & Rotlichttherapie-Discount: 5 % mit LONGEVITY (Mai 2026)",
  description:
    "heilys Rabattcode LONGEVITY: 5 % Rotlichttherapie-Discount sofort im Checkout ✓ Cashback-Alternative ohne Gutscheinportal ✓ Erfahrungen, Gen30 & Trustpilot-Score 4,3/5 (Mai 2026).",
  hypothesis:
    "Rabattcode-First Framing zieht primär Coupon-Sucher; Discount-Begriff in Title für Pos. 1.3 Keyword.",
};

// Variante B: Discount-First + Konkretes Sparbeispiel statt nur "5 %"
const VARIANT_B: SeoVariant = {
  id: "b",
  title:
    "Rotlichttherapie-Discount 2026: 5 % heilys Rabatt mit Code LONGEVITY",
  description:
    "5 % Rotlichttherapie-Discount auf alle heilys Geräte: Code LONGEVITY direkt im Checkout einlösen ✓ Spart bis zu 75 € beim Gen30+ Vision ✓ Erfahrungen & Trustpilot 4,3/5.",
  hypothesis:
    "Discount-First passt besser zur GSC-Query 'rotlichttherapie discount' (Pos. 1.3, 0% CTR). Konkretes € Sparbeispiel erhöht CTR.",
};

const VARIANTS: Record<SeoVariantId, SeoVariant> = { a: VARIANT_A, b: VARIANT_B };

/**
 * Anker-Datum: ab hier startet Variante A. Alle 14 Tage Wechsel.
 * Zeitzone irrelevant, weil Tagesgenauigkeit reicht.
 */
const EXPERIMENT_ANCHOR = new Date("2026-05-01T00:00:00Z").getTime();
const SWITCH_INTERVAL_DAYS = 14;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Manueller Override (z. B. zum Testen): ?seoVariant=b */
function getOverride(): SeoVariantId | null {
  if (typeof window === "undefined") return null;
  try {
    const url = new URL(window.location.href);
    const v = url.searchParams.get("seoVariant");
    if (v === "a" || v === "b") return v;
  } catch {
    // ignore
  }
  return null;
}

export function getActiveVariant(now: Date = new Date()): SeoVariant {
  const override = getOverride();
  if (override) return VARIANTS[override];

  const diffDays = Math.floor((now.getTime() - EXPERIMENT_ANCHOR) / MS_PER_DAY);
  // Negative Tage (vor Anker) -> Variante A
  const cycle = Math.floor(Math.max(0, diffDays) / SWITCH_INTERVAL_DAYS);
  return cycle % 2 === 0 ? VARIANT_A : VARIANT_B;
}

/** Aktuelles Variant-Fenster als ISO-Strings – zum Markieren in Auswertung. */
export function getActiveVariantWindow(now: Date = new Date()): {
  start: string;
  end: string;
} {
  const diffDays = Math.floor((now.getTime() - EXPERIMENT_ANCHOR) / MS_PER_DAY);
  const cycle = Math.floor(Math.max(0, diffDays) / SWITCH_INTERVAL_DAYS);
  const start = EXPERIMENT_ANCHOR + cycle * SWITCH_INTERVAL_DAYS * MS_PER_DAY;
  const end = start + SWITCH_INTERVAL_DAYS * MS_PER_DAY - 1;
  return {
    start: new Date(start).toISOString().slice(0, 10),
    end: new Date(end).toISOString().slice(0, 10),
  };
}
